import { z } from 'zod';
import { BoxFormatEnum, BoxOwnerView } from './box.js';
import { AnswerView } from './answer.js';

/**
 * Схемы Inbox (`GET /inbox`) для API контракта (Веха 3).
 *
 * Источник правды:
 *   - CANON §2.B (анонимность) + §10 SP4: `QuestionInboxItem` НЕ содержит
 *     `senderFingerprint`. Сервер обязан select'ить без него.
 *   - CANON §2.D (zod-strict): все .strict() — unknown поля отвергаются.
 *   - threat-models/api.md → I (fingerprint leak), D (limit cap 50).
 *
 * Курсорная пагинация: `cursor` — id последнего элемента предыдущей страницы;
 * сортировка `createdAt desc, id desc` фиксируется на сервере. `limit.max(50)`
 * защищает от дорогих запросов (threat-models/api.md → D).
 *
 * `format` (Трек D, work-036) — опциональный: резолвит бокс автора по
 * (ownerId, format) вместо старейшего бокса. Без параметра — обратная
 * совместимость: старейший бокс автора (исторически всегда 'ask').
 */
export const InboxQuery = z
  .object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    format: BoxFormatEnum.optional(),
  })
  .strict();
export type InboxQuery = z.infer<typeof InboxQuery>;

/**
 * Статусы Question (зеркало `Question.status` в Prisma schema).
 * Веха 7 расширит moderationFlag поведение, но enum-значения уже зафиксированы.
 */
export const QuestionStatus = z.enum(['pending', 'answered', 'hidden', 'removed']);
export type QuestionStatus = z.infer<typeof QuestionStatus>;

export const QuestionModerationFlag = z.enum(['flagged', 'blocked']);
export type QuestionModerationFlag = z.infer<typeof QuestionModerationFlag>;

/**
 * Item inbox'а — БЕЗ `senderFingerprint` (CANON SP4).
 * `answer` nullable: вопрос может быть pending (без ответа).
 */
export const QuestionInboxItem = z
  .object({
    id: z.string(),
    text: z.string(),
    status: QuestionStatus,
    moderationFlag: QuestionModerationFlag.nullable(),
    createdAt: z.string().datetime(),
    answer: AnswerView.nullable(),
  })
  .strict();
export type QuestionInboxItem = z.infer<typeof QuestionInboxItem>;

/**
 * Response `GET /inbox`. `nextCursor` null если следующей страницы нет.
 * `box` — owner view (с deepLink) первого активного бокса автора (MVP: один
 * автор = один активный бокс).
 */
export const InboxResponse = z
  .object({
    box: BoxOwnerView,
    items: z.array(QuestionInboxItem),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type InboxResponse = z.infer<typeof InboxResponse>;
